import { zodResolver } from "@hookform/resolvers/zod"
import { circuitBreakerOptions, hasOnlyHighTariff } from "@nano-portal/customers"
import { EAN_PREFIX } from "@nano-portal/shared"
import { ReactNode, useEffect } from "react"
import { Controller, DefaultValues, FormProvider, useForm } from "react-hook-form"
import { Div } from "../../../../../components/div/Div"
import { Flex } from "../../../../../components/flex/Flex"
import { Spacer } from "../../../../../components/spacer/Spacer"
import { Span } from "../../../../../components/span/Span"
import Text from "../../../../../components/Text"
import { CSSProp, theme } from "../../../../../styles/stitches.config"
import { getFormError, getFormSuccess } from "../../../../../utils/form"
import { SResponsiveRow } from "../../acquisitionForm/AcquisitionForm.styled"
import { CustomerType, Product } from "../../acquisitionForm/form/useAcquisitionFormSchema"
import { DistributionAreaSelect } from "../components/DistributionAreaSelect"
import { DistributionTariffSelect } from "../components/DistributionTariffSelect"
import { EanInput } from "../components/EanInput"
import { FileInput } from "../components/FileInput"
import { FormItem, FormLabel } from "../components/Form"
import { FormInput, InputDisclaimer } from "../components/Input"
import { NavigationButtons } from "../components/NavigationButtons"
import { FormSelect } from "../components/Select"
import { Subheader } from "../components/Subheader"
import { MAX_FILE_SIZE_MB } from "../utils"
import { ConsumptionPointContractInfoSection } from "./ConsumptionPointContractInfoSection"
import { consumptionPointFormSchema, ConsumptionPointFormValues } from "./consumptionPointForm.utils"

type InlineLabeledProps = {
  input: ReactNode
  label: string
  css?: CSSProp
}

function InlineLabeledInput(props: InlineLabeledProps) {
  const { input, label, css } = props
  return (
    <Flex css={{ gap: theme.space.s6, ...css }}>
      {input}
      <Text type="headerH4" css={{ marginTop: 24 }}>
        {label}
      </Text>
    </Flex>
  )
}

type Props = {
  customerType: CustomerType
  selectedProduct: Product
  defaultValues?: DefaultValues<ConsumptionPointFormValues>
  onReturn?: () => void
  onSubmit?: (data: ConsumptionPointFormValues) => void
}

export function ConsumptionPointForm(props: Props) {
  const { defaultValues, onSubmit, onReturn, selectedProduct, customerType } = props
  const currentContractDefaultValues =
    defaultValues?.contractInfo?.terminationOption === "TERMINATE"
      ? defaultValues.contractInfo.currentContract
      : undefined

  const form = useForm<ConsumptionPointFormValues>({
    resolver: zodResolver(consumptionPointFormSchema),
    mode: "onBlur",
    defaultValues: {
      ean: "",
      circuitBreakerPhases: "1",
      currentSupplier: "",
      contractInfo: defaultValues?.contractInfo ?? {
        terminationOption: "TERMINATE",
        currentContract: {
          contractType: "VARIABLE_TERM",
          noticePeriod: "ONE_MONTH",
          ...currentContractDefaultValues,
        },
      },
      ...defaultValues,
    },
  })

  const distributionTariff = form.watch("distributionTariff")
  const lastBillingWatch = form.watch("lastBilling")
  const contractTerminationWatch = form.watch("contractInfo.contractTermination")

  const singleTariffSelected = hasOnlyHighTariff(distributionTariff)

  useEffect(() => {
    if (singleTariffSelected) {
      form.resetField("lowTariffConsumptionKWh")
    }
  }, [form, singleTariffSelected])

  function handleSumbit(formValues: ConsumptionPointFormValues) {
    // assigning watch value since watch returns the File object itself and submitted parsed values return plain
    // object wich loses the required properties of a File object for later download
    formValues.lastBilling = lastBillingWatch
    if (
      formValues.contractInfo.terminationOption === "DO_NOT_TERMINATE" &&
      formValues.contractInfo.contractTermination
    ) {
      formValues.contractInfo.contractTermination = contractTerminationWatch
    }

    onSubmit?.(formValues)
  }

  return (
    <FormProvider {...form}>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={form.handleSubmit(handleSumbit)}>
        <>
          <Subheader asSectionHeader>Informace o odběrném místě</Subheader>
          <Spacer size={theme.space.s6} />
          <Controller
            control={form.control}
            name="ean"
            render={({ field }) => (
              <FormItem>
                <FormLabel>EAN</FormLabel>
                <EanInput
                  id="ean"
                  prefix={EAN_PREFIX}
                  {...field}
                  error={getFormError({ form, name: "ean" })}
                  success={getFormSuccess({ form, name: "ean" })}
                />
              </FormItem>
            )}
          />

          <Spacer size={theme.space.s4_5} />

          <InputDisclaimer label="EAN je identifikační číslo vašeho odběrného místa, naleznete ho ve vašem vyúčtování za elektřinu. " />

          <Spacer size={theme.space.s10} />

          <SResponsiveRow css={{ gap: theme.space.s11 }}>
            <Div css={{ width: "100%", "@md": { width: "75%" } }}>
              <Controller
                control={form.control}
                name="distributionTariff"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="distributionTariff">Distribuční sazba</FormLabel>
                    <DistributionTariffSelect
                      id="distributionTariff"
                      {...field}
                      error={getFormError({ form, name: "distributionTariff" })}
                      success={getFormSuccess({ form, name: "distributionTariff" })}
                      displayBusinessTariffs={customerType === "business"}
                    />
                  </FormItem>
                )}
              />

              <Spacer size={theme.space.s2} />

              <InputDisclaimer label="Distribuční sazbu naleznete ve svém vyúčtování ve tvaru Dxxd" />
            </Div>

            <Div css={{ width: "100%", "@md": { width: "initial" } }}>
              <FormLabel>Hodnota jističe</FormLabel>
              <Flex align="start" css={{ gap: theme.space.s6 }}>
                <InlineLabeledInput
                  label="X"
                  css={{ width: "50%" }}
                  input={
                    <Controller
                      control={form.control}
                      name="circuitBreakerPhases"
                      render={({ field }) => (
                        <FormItem css={{ width: "100%" }}>
                          <FormSelect
                            id="circuitBreakerPhases"
                            options={circuitBreakerOptions}
                            {...field}
                            error={getFormError({ form, name: "circuitBreakerPhases" })}
                            success={getFormSuccess({ form, name: "circuitBreakerPhases" })}
                          />
                        </FormItem>
                      )}
                    />
                  }
                />

                <InlineLabeledInput
                  label="A"
                  css={{ width: "50%" }}
                  input={
                    <FormInput
                      id="circuit-breaker-value"
                      type="number"
                      placeholder="např. 42.5"
                      {...form.register("circuitBreakerCapacity", { valueAsNumber: true })}
                      error={getFormError({ form, name: "circuitBreakerCapacity" })}
                      success={getFormSuccess({ form, name: "circuitBreakerCapacity" })}
                    />
                  }
                />
              </Flex>

              <Spacer size={theme.space.s2} />

              <InputDisclaimer label="Hodnotu jističe naleznete ve vašem vyúčtování, údaj bude uveden v ampérech (A)" />
            </Div>
          </SResponsiveRow>

          <Spacer size={theme.space.s9} />

          <SResponsiveRow>
            <Div css={{ width: "100%" }}>
              <FormItem>
                <FormLabel>Výše roční spotřeby VT</FormLabel>
                <InlineLabeledInput
                  label="kWh"
                  input={
                    <FormInput
                      id="annual-consumption-ht"
                      type="number"
                      placeholder="např. 2568"
                      {...form.register("highTariffConsumptionKWh", { valueAsNumber: true })}
                      error={getFormError({ form, name: "highTariffConsumptionKWh" })}
                      success={getFormSuccess({ form, name: "highTariffConsumptionKWh" })}
                    />
                  }
                />
              </FormItem>

              <Spacer size={theme.space.s2} />

              <InputDisclaimer label="Hodnotu vaši roční spotřeby naleznete ve vašem vyúčtování" />
            </Div>

            <Div css={{ width: "100%" }}>
              <FormItem>
                <FormLabel>Výše roční spotřeby NT</FormLabel>
                <InlineLabeledInput
                  label="kWh"
                  input={
                    <FormInput
                      id="annual-consumption-lt"
                      type="number"
                      placeholder="např. 2568"
                      disabled={singleTariffSelected}
                      {...form.register("lowTariffConsumptionKWh", { valueAsNumber: true })}
                      error={getFormError({ form, name: "lowTariffConsumptionKWh" })}
                      success={getFormSuccess({ form, name: "lowTariffConsumptionKWh" })}
                    />
                  }
                />
              </FormItem>

              <Spacer size={theme.space.s2} />

              <InputDisclaimer label="Hodnotu vaši roční spotřeby naleznete ve vašem vyúčtování" />
            </Div>
          </SResponsiveRow>
          <Spacer size={theme.space.s9} />

          <Controller
            control={form.control}
            name="distributionArea"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Distribuční oblast</FormLabel>
                <DistributionAreaSelect
                  id="distributionArea"
                  {...field}
                  error={getFormError({ form, name: "distributionArea" })}
                  success={getFormSuccess({ form, name: "distributionArea" })}
                />
              </FormItem>
            )}
          />

          <Spacer size={theme.space.s2} />
          <InputDisclaimer
            labelCSS={{ maxWidth: "510px" }}
            label="V Česku jsou tři distribuční oblasti s mírně odlišnými cenami. Podle vámi zadaného kraje zjistíme, kam patříte."
          />
          <Spacer size={theme.space.s9} />

          <ConsumptionPointContractInfoSection />

          <Spacer size={theme.space.s9} />

          <Controller
            control={form.control}
            name="lastBilling"
            render={({ field: { value, onChange, ref } }) => {
              const fileError = getFormError({ form, name: "lastBilling" })
              const typeError = getFormError({ form, name: "lastBilling.type" })
              const sizeError = getFormError({ form, name: "lastBilling.size" })

              const errorMessage = fileError ?? typeError ?? sizeError

              return (
                <FormItem>
                  <FormLabel>Poslední vyúčtování spotřeby</FormLabel>
                  <FileInput
                    id="lastBilling"
                    uploadedFileName={errorMessage ? undefined : value?.name}
                    error={errorMessage}
                    ref={ref}
                    onClear={() => form.resetField("lastBilling", { defaultValue: "" })}
                    onChange={(e) => {
                      onChange(e.target.files?.[0])
                    }}
                  />
                </FormItem>
              )
            }}
          />
          <Spacer size={theme.space.s3} />

          <InputDisclaimer
            label={
              <p>
                Nahrajte soubor <Span css={{ fontWeight: theme.fontWeights.semiBold }}>PDF nebo obrázek</Span> o
                maximální velikosti <Span css={{ fontWeight: theme.fontWeights.semiBold }}>{MAX_FILE_SIZE_MB} MB</Span>.
              </p>
            }
          />
        </>

        <Spacer size={theme.space.s17} />

        <NavigationButtons
          prevText="Osobní údaje"
          nextText={selectedProduct === "productionAndConsumption" ? "Výrobní místo" : "Platební údaje"}
          onReturn={onReturn}
          isSubmitting={form.formState.isSubmitting}
        />
      </form>
    </FormProvider>
  )
}
